import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Skeleton } from 'boneyard-js/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ColumnVisibilityToggle } from '../../../components/ColumnVisibilityToggle'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DatePicker } from '../../../components/ui/date-picker'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { MonthPicker } from '../../../components/ui/month-picker'
import {
  type ColumnVisibilityDef,
  useColumnVisibility,
} from '../../../hooks/useColumnVisibility'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import { supabase } from '../../../lib/supabase'
import {
  monthEnd,
  monthStart,
  toYearMonth,
} from '../monthly-records/monthlyRecordsHelpers'
import type {
  ContractOption,
  RoomOption,
  UtilityRecordFormValues,
  UtilityRecordRow,
} from './interfaces/UtilityRecord'

const PAGE_SIZE = 20

const HIDDEN_COLS_KEY = 'utility-records-hidden-cols'

const COLUMN_DEFS: ColumnVisibilityDef[] = [
  { id: 'room_number', label: 'Room', alwaysVisible: true },
  { id: 'record_date', label: 'Date' },
  { id: 'elec_units', label: 'Elec (units)' },
  { id: 'elec_total', label: 'Elec Total (฿)' },
  { id: 'water_units', label: 'Water (units)' },
  { id: 'water_total', label: 'Water Total (฿)' },
  { id: 'contract_id', label: 'Contract ID' },
  { id: 'elec_unit_price', label: 'Elec Price' },
  { id: 'elec_prev_reading', label: 'Elec Prev' },
  { id: 'elec_curr_reading', label: 'Elec Curr' },
  { id: 'water_unit_price', label: 'Water Price' },
  { id: 'water_prev_reading', label: 'Water Prev' },
  { id: 'water_curr_reading', label: 'Water Curr' },
  { id: 'created_at', label: 'Created At' },
  { id: 'updated_at', label: 'Updated At' },
  { id: 'payment_status', label: 'Payment' },
]

type SortColumn =
  | 'room_number'
  | 'record_date'
  | 'elec_units'
  | 'elec_total'
  | 'water_units'
  | 'water_total'
  | 'contract_id'
  | 'elec_unit_price'
  | 'elec_prev_reading'
  | 'elec_curr_reading'
  | 'water_unit_price'
  | 'water_prev_reading'
  | 'water_curr_reading'
  | 'created_at'
  | 'updated_at'
  | 'payment_status'

type SortRule = {
  column: SortColumn
  ascending: boolean
}

const SORT_COLUMNS: Array<{
  key: SortColumn
  label: string
  align: 'left' | 'right'
}> = [
  { key: 'room_number', label: 'Room', align: 'left' },
  { key: 'record_date', label: 'Date', align: 'left' },
  { key: 'elec_units', label: 'Elec (units)', align: 'right' },
  { key: 'elec_total', label: 'Elec Total (฿)', align: 'right' },
  { key: 'water_units', label: 'Water (units)', align: 'right' },
  { key: 'water_total', label: 'Water Total (฿)', align: 'right' },
  { key: 'contract_id', label: 'Contract ID', align: 'left' },
  { key: 'elec_unit_price', label: 'Elec Price', align: 'right' },
  { key: 'elec_prev_reading', label: 'Elec Prev', align: 'right' },
  { key: 'elec_curr_reading', label: 'Elec Curr', align: 'right' },
  { key: 'water_unit_price', label: 'Water Price', align: 'right' },
  { key: 'water_prev_reading', label: 'Water Prev', align: 'right' },
  { key: 'water_curr_reading', label: 'Water Curr', align: 'right' },
  { key: 'created_at', label: 'Created At', align: 'left' },
  { key: 'updated_at', label: 'Updated At', align: 'left' },
  { key: 'payment_status', label: 'Payment', align: 'left' },
]

const utilitySchema = z.object({
  room_number: z.string().min(1, 'Room is required'),
  contract_id: z.string().uuid().nullable().optional(),
  elec_unit_price: z.coerce.number().min(0),
  elec_prev_reading: z.coerce.number().int().min(0),
  elec_curr_reading: z.coerce.number().int().min(0),
  water_unit_price: z.coerce.number().min(0),
  water_prev_reading: z.coerce.number().int().min(0),
  water_curr_reading: z.coerce.number().int().min(0),
  record_date: z.string().min(1, 'Record date is required'),
  payment_status: z.enum(['paid', 'unpaid']),
  remark: z.string().optional(),
})

const emptyForm = (): UtilityRecordFormValues => ({
  room_number: '',
  contract_id: null,
  elec_unit_price: '0',
  elec_prev_reading: '0',
  elec_curr_reading: '0',
  water_unit_price: '0',
  water_prev_reading: '0',
  water_curr_reading: '0',
  record_date: new Date().toISOString().split('T')[0],
  payment_status: 'unpaid',
  remark: '',
})

const rowToForm = (row: UtilityRecordRow): UtilityRecordFormValues => ({
  room_number: row.room_number,
  contract_id: row.contract_id ?? null,
  elec_unit_price: String(row.elec_unit_price),
  elec_prev_reading: String(row.elec_prev_reading),
  elec_curr_reading: String(row.elec_curr_reading),
  water_unit_price: String(row.water_unit_price),
  water_prev_reading: String(row.water_prev_reading),
  water_curr_reading: String(row.water_curr_reading),
  record_date: row.record_date,
  payment_status: row.payment_status as 'paid' | 'unpaid',
  remark: row.remark ?? '',
})

export default function UtilityRecordsPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialMonth = searchParams.get('month') || toYearMonth(new Date())
  const [filterMonth, setFilterMonth] = useState<string>(initialMonth)
  const [showForm, setShowForm] = useState(false)
  const showHeader = searchParams.get('header') !== 'hide'
  const [editingRecord, setEditingRecord] = useState<UtilityRecordRow | null>(
    null,
  )
  const [form, setForm] = useState<UtilityRecordFormValues>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showMarkPaid, setShowMarkPaid] = useState(false)
  const [selectedRoomForForm, setSelectedRoomForForm] = useState<string | null>(
    null,
  )
  const [sortRules, setSortRules] = useState<SortRule[]>([
    { column: 'room_number', ascending: true },
    { column: 'record_date', ascending: false },
  ])
  const [paymentFilter, setPaymentFilter] = useState<string>('all')
  const [roomSearch, setRoomSearch] = useState('')
  const debouncedRoomSearch = useDebouncedValue(roomSearch, 500)
  const normalizedRoomSearch = debouncedRoomSearch.trim()

  const isLocked = filterMonth === '2026-07'

  const handleSort = (column: SortColumn) => {
    setSortRules((prev) => {
      const existingIndex = prev.findIndex((rule) => rule.column === column)
      if (existingIndex === 0) {
        return [{ column, ascending: !prev[0].ascending }, ...prev.slice(1)]
      }

      const remaining = prev.filter((rule) => rule.column !== column)
      return [{ column, ascending: true }, ...remaining.slice(0, 1)]
    })
  }

  const swapSortPriority = () => {
    setSortRules((prev) => {
      if (prev.length < 2) return prev
      const [primary, secondary, ...rest] = prev
      return [secondary, primary, ...rest]
    })
  }

  const sortIndicator = (column: SortColumn) => {
    const index = sortRules.findIndex((rule) => rule.column === column)
    if (index === -1) return ' ⇅'
    const arrow = sortRules[index].ascending ? ' ↑' : ' ↓'
    return index === 0 ? arrow : `${arrow}${index + 1}`
  }

  const {
    ref: colsDropdownRef,
    open: colsOpen,
    setOpen: setColsOpen,
    hiddenCount,
    isVisible,
    toggleColumn,
  } = useColumnVisibility(HIDDEN_COLS_KEY, COLUMN_DEFS)

  const col = isVisible

  const getSortLabel = (column: SortColumn) =>
    SORT_COLUMNS.find((option) => option.key === column)?.label ?? column

  const sortKey = sortRules.map((rule) => `${rule.column}:${rule.ascending}`)

  // Sync filterMonth state when URL changes (back/forward navigation)
  useEffect(() => {
    const urlMonth = searchParams.get('month')
    if (urlMonth && urlMonth !== filterMonth) {
      setFilterMonth(urlMonth)
    }
  }, [searchParams, filterMonth])

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        'utility_records',
        sortKey,
        filterMonth,
        paymentFilter,
        normalizedRoomSearch,
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const from = pageParam
        const to = from + PAGE_SIZE - 1
        let query = supabase
          .from('utility_records')
          .select('*')
          .is('deleted_at', null)
        for (const rule of sortRules) {
          query = query.order(rule.column, { ascending: rule.ascending })
        }
        if (filterMonth) {
          query = query
            .gte('record_date', monthStart(filterMonth))
            .lte('record_date', monthEnd(filterMonth))
        }
        // Apply payment status filter if not 'all'
        if (paymentFilter !== 'all') {
          query = query.eq('payment_status', paymentFilter)
        }
        if (normalizedRoomSearch) {
          query = query.eq('room_number', normalizedRoomSearch)
        }
        const { data: rows, error } = await query.range(from, to)
        if (error) throw error
        return rows as UtilityRecordRow[]
      },
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < PAGE_SIZE ? undefined : lastPageParam + PAGE_SIZE,
    })

  const records: UtilityRecordRow[] = data?.pages.flat() ?? []

  // Query for payment status counts
  const { data: paymentCounts } = useQuery({
    queryKey: [
      'utility_records',
      'payment-counts',
      filterMonth,
      normalizedRoomSearch,
    ],
    queryFn: async () => {
      let query = supabase
        .from('utility_records')
        .select('payment_status')
        .is('deleted_at', null)
      if (filterMonth) {
        query = query
          .gte('record_date', monthStart(filterMonth))
          .lte('record_date', monthEnd(filterMonth))
      }
      if (normalizedRoomSearch) {
        query = query.eq('room_number', normalizedRoomSearch)
      }
      const { data, error } = await query

      if (error) {
        console.error('Error fetching payment counts:', error)
        throw error
      }

      const counts = {
        paid: 0,
        unpaid: 0,
      }

      for (const record of data ?? []) {
        if (record.payment_status in counts) {
          counts[record.payment_status as keyof typeof counts]++
        }
      }

      return counts
    },
  })

  const selectedRows = records.filter((rec) => selectedIds.includes(rec.id))
  const selectedRoomNumbers = Array.from(
    new Set(selectedRows.map((rec) => rec.room_number)),
  )
  const areAllVisibleSelected =
    records.length > 0 && records.every((rec) => selectedIds.includes(rec.id))

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id],
    )
  }

  const toggleSelectAll = () => {
    const visibleIds = records.map((rec) => rec.id)
    setSelectedIds(areAllVisibleSelected ? [] : visibleIds)
  }

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const sentinelRef = useInfiniteScroll(onLoadMore, Boolean(hasNextPage))

  const { data: rooms = [] } = useQuery<RoomOption[]>({
    queryKey: ['rooms', 'options'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('rooms')
        .select('room_number, elec_unit_price, water_unit_price')
        .order('room_number')
      if (error) throw error
      return rows as RoomOption[]
    },
  })

  const { data: latestRoomRecord } = useQuery({
    queryKey: ['utility_records', 'latest', selectedRoomForForm],
    enabled: !!selectedRoomForForm,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('utility_records')
        .select('elec_curr_reading, water_curr_reading, record_date')
        .eq('room_number', selectedRoomForForm ?? '')
        .order('record_date', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data
    },
  })

  const { data: activeContracts = [] } = useQuery<ContractOption[]>({
    queryKey: ['contracts', 'active'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('contracts')
        .select('id, room_number, tenant_id')
        .eq('status', 'active')
      if (error) throw error
      return rows as ContractOption[]
    },
  })

  // When a new room is chosen on the create form, auto-fill known values
  const handleRoomChange = (roomNumber: string) => {
    setForm((prev) => {
      const room = rooms.find((r) => r.room_number === roomNumber)
      const contract = activeContracts.find((c) => c.room_number === roomNumber)
      return {
        ...prev,
        room_number: roomNumber,
        contract_id: contract?.id ?? null,
        elec_unit_price: room
          ? String(room.elec_unit_price)
          : prev.elec_unit_price,
        water_unit_price: room
          ? String(room.water_unit_price)
          : prev.water_unit_price,
      }
    })
    setSelectedRoomForForm(roomNumber || null)
  }

  // Apply prev readings once the latest-record query resolves (create mode only)
  useEffect(() => {
    if (editingRecord) return
    if (!latestRoomRecord) return
    setForm((prev) => ({
      ...prev,
      elec_prev_reading: String(latestRoomRecord.elec_curr_reading),
      water_prev_reading: String(latestRoomRecord.water_curr_reading),
    }))
  }, [latestRoomRecord, editingRecord])

  const createMutation = useMutation({
    mutationFn: async (values: UtilityRecordFormValues) => {
      const parsed = utilitySchema.parse(values)
      const payload = {
        ...parsed,
        contract_id: parsed.contract_id || null,
      }
      const { error } = await supabase.from('utility_records').insert(payload)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility_records'] })
      setShowForm(false)
      setForm(emptyForm())
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id: string
      values: UtilityRecordFormValues
    }) => {
      const parsed = utilitySchema.parse(values)
      const payload = {
        ...parsed,
        contract_id: parsed.contract_id || null,
      }
      const { error } = await supabase
        .from('utility_records')
        .update(payload)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility_records'] })
      setShowForm(false)
      setEditingRecord(null)
      setForm(emptyForm())
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('utility_records')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility_records'] })
      setDeleteTarget(null)
    },
  })

  const markPaidMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return
      const { error } = await supabase
        .from('utility_records')
        .update({ payment_status: 'paid' })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility_records'] })
      setShowMarkPaid(false)
      setSelectedIds([])
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = utilitySchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        fieldErrors[issue.path[0] as string] = issue.message
      }
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    if (editingRecord) {
      updateMutation.mutate({ id: editingRecord.id, values: form })
    } else {
      createMutation.mutate(form)
    }
  }

  const openCreate = () => {
    setEditingRecord(null)
    setForm(emptyForm())
    setErrors({})
    setSelectedRoomForForm(null)
    setShowForm(true)
  }

  const openEdit = (record: UtilityRecordRow) => {
    setEditingRecord(record)
    setForm(rowToForm(record))
    setErrors({})
    setShowForm(true)
  }

  const isFormDirty = useMemo((): boolean => {
    if (!showForm) return false
    if (editingRecord) {
      const orig = rowToForm(editingRecord)
      return (
        form.room_number !== orig.room_number ||
        form.contract_id !== orig.contract_id ||
        form.elec_unit_price !== orig.elec_unit_price ||
        form.elec_prev_reading !== orig.elec_prev_reading ||
        form.elec_curr_reading !== orig.elec_curr_reading ||
        form.water_unit_price !== orig.water_unit_price ||
        form.water_prev_reading !== orig.water_prev_reading ||
        form.water_curr_reading !== orig.water_curr_reading ||
        form.record_date !== orig.record_date ||
        form.payment_status !== orig.payment_status ||
        form.remark !== orig.remark
      )
    }
    const empty = emptyForm()
    return (
      form.room_number !== (selectedRoomForForm ?? empty.room_number) ||
      form.contract_id !== empty.contract_id ||
      form.elec_unit_price !== empty.elec_unit_price ||
      (latestRoomRecord
        ? form.elec_prev_reading !== String(latestRoomRecord.elec_curr_reading)
        : form.elec_prev_reading !== empty.elec_prev_reading) ||
      form.elec_curr_reading !== empty.elec_curr_reading ||
      form.water_unit_price !== empty.water_unit_price ||
      (latestRoomRecord
        ? form.water_prev_reading !==
          String(latestRoomRecord.water_curr_reading)
        : form.water_prev_reading !== empty.water_prev_reading) ||
      form.water_curr_reading !== empty.water_curr_reading ||
      form.payment_status !== empty.payment_status ||
      form.remark !== empty.remark
    )
  }, [showForm, form, editingRecord, selectedRoomForForm, latestRoomRecord])

  const { isBlocked, proceed, reset } = useUnsavedChanges(isFormDirty)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false)

  const closeForm = () => {
    setShowForm(false)
    setEditingRecord(null)
    setForm(emptyForm())
    setErrors({})
    setSelectedRoomForForm(null)
  }

  const handleCancelForm = (): void => {
    if (isFormDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  function renderHeader() {
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">
              Utility Records
            </h2>
            <p className="text-gray-500 text-xs">
              {records.length} record{records.length !== 1 ? 's' : ''} ·{' '}
              {filterMonth}
            </p>
            {paymentCounts && (
              <div className="mt-2 flex gap-3 text-xs">
                <span className="rounded bg-green-100 px-2 py-1 text-green-700">
                  Paid: {paymentCounts.paid}
                </span>
                <span className="rounded bg-red-100 px-2 py-1 text-red-700">
                  Unpaid: {paymentCounts.unpaid}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={openCreate} disabled={isLocked}>
              + New Record
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowMarkPaid(true)}
              disabled={selectedIds.length === 0 || isLocked}
            >
              Mark Paid
            </Button>
            {selectedIds.length > 0 && (
              <span className="text-gray-500 text-xs">
                {selectedIds.length} selected from {selectedRoomNumbers.length}{' '}
                room{selectedRoomNumbers.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
          <ColumnVisibilityToggle
            title="Columns"
            columns={COLUMN_DEFS}
            isOpen={colsOpen}
            hiddenCount={hiddenCount}
            wrapperRef={colsDropdownRef}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onToggleOpen={() => setColsOpen((o) => !o)}
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="room-search" className="text-xs">
              Search:
            </Label>
            <Input
              id="room-search"
              type="search"
              placeholder="101"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              className="h-7 w-28 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="month-filter" className="font-medium text-gray-600">
              Month
            </label>
            <MonthPicker
              id="month-filter"
              value={filterMonth}
              onValueChange={(value) => {
                setFilterMonth(value)
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev)
                  params.set('month', value)
                  return params
                })
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="payment-filter" className="text-xs">
              Payment:
            </Label>
            <select
              id="payment-filter"
              title="Filter by payment status"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <span>
            Primary: {getSortLabel(sortRules[0].column)}{' '}
            {sortRules[0].ascending ? '↑' : '↓'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={swapSortPriority}
            disabled={!sortRules[1]}
          >
            Swap
          </Button>
          <span>
            Secondary:{' '}
            {sortRules[1]
              ? `${getSortLabel(sortRules[1].column)} ${sortRules[1].ascending ? '↑' : '↓'}`
              : 'None'}
          </span>
        </div>
      </>
    )
  }

  return (
    <div className="relative flex h-full flex-col">
      {showHeader && (
        <div className="sticky top-0 z-30 bg-gray-50">{renderHeader()}</div>
      )}

      <Skeleton
        name="admin-utility-records"
        loading={isLoading}
        className="flex min-h-0 flex-1 flex-col *:data-boneyard-content:flex *:data-boneyard-content:min-h-0 *:data-boneyard-content:flex-1 *:data-boneyard-content:flex-col"
      >
        <Card className="min-h-0 flex-1 overflow-hidden p-0">
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="sticky top-0 z-20 bg-gray-50">
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={areAllVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={isLocked}
                      title="Select all visible records"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </th>
                  {(
                    [
                      { key: 'room_number', label: 'Room', align: 'left' },
                      { key: 'record_date', label: 'Date', align: 'left' },
                      {
                        key: 'elec_units',
                        label: 'Elec (units)',
                        align: 'right',
                      },
                      {
                        key: 'elec_total',
                        label: 'Elec Total (฿)',
                        align: 'right',
                      },
                      {
                        key: 'water_units',
                        label: 'Water (units)',
                        align: 'right',
                      },
                      {
                        key: 'water_total',
                        label: 'Water Total (฿)',
                        align: 'right',
                      },
                      {
                        key: 'contract_id',
                        label: 'Contract ID',
                        align: 'left',
                      },
                      {
                        key: 'elec_unit_price',
                        label: 'Elec Price',
                        align: 'right',
                      },
                      {
                        key: 'elec_prev_reading',
                        label: 'Elec Prev',
                        align: 'right',
                      },
                      {
                        key: 'elec_curr_reading',
                        label: 'Elec Curr',
                        align: 'right',
                      },
                      {
                        key: 'water_unit_price',
                        label: 'Water Price',
                        align: 'right',
                      },
                      {
                        key: 'water_prev_reading',
                        label: 'Water Prev',
                        align: 'right',
                      },
                      {
                        key: 'water_curr_reading',
                        label: 'Water Curr',
                        align: 'right',
                      },
                      {
                        key: 'created_at',
                        label: 'Created At',
                        align: 'left',
                      },
                      {
                        key: 'updated_at',
                        label: 'Updated At',
                        align: 'left',
                      },
                      {
                        key: 'payment_status',
                        label: 'Payment',
                        align: 'left',
                      },
                    ] as {
                      key: SortColumn
                      label: string
                      align: 'left' | 'right'
                    }[]
                  )
                    .filter(({ key }) => col(key))
                    .map(({ key, label, align }) => (
                      <th
                        key={key}
                        className={`px-4 py-3 font-medium text-gray-500 text-${align} ${
                          key === 'room_number'
                            ? 'sticky left-0 z-10 bg-gray-50'
                            : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(key)}
                          className={`inline-flex cursor-pointer items-center gap-0.5 hover:text-gray-900 ${
                            sortRules[0]?.column === key ? 'text-gray-900' : ''
                          }`}
                        >
                          {label}
                          <span className="text-xs">{sortIndicator(key)}</span>
                        </button>
                      </th>
                    ))}
                  <th className="px-4 py-3 text-left font-medium text-gray-500">
                    Remark
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {records.map((rec) => (
                  <tr key={rec.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(rec.id)}
                        onChange={() => toggleSelect(rec.id)}
                        disabled={isLocked}
                        title={`Select record ${rec.id}`}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                    </td>
                    <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-4 py-3 font-medium">
                      {rec.room_number}
                    </td>
                    {col('record_date') && (
                      <td className="px-4 py-3">{rec.record_date}</td>
                    )}
                    {col('elec_units') && (
                      <td className="px-4 py-3 text-right">{rec.elec_units}</td>
                    )}
                    {col('elec_total') && (
                      <td className="px-4 py-3 text-right">
                        {Number(rec.elec_total).toLocaleString()}
                      </td>
                    )}
                    {col('water_units') && (
                      <td className="px-4 py-3 text-right">
                        {rec.water_units}
                      </td>
                    )}
                    {col('water_total') && (
                      <td className="px-4 py-3 text-right">
                        {Number(rec.water_total).toLocaleString()}
                      </td>
                    )}
                    {col('contract_id') && (
                      <td className="px-4 py-3 text-left">
                        {rec.contract_id?.substring(0, 8) ?? '—'}
                      </td>
                    )}
                    {col('elec_unit_price') && (
                      <td className="px-4 py-3 text-right">
                        {rec.elec_unit_price}
                      </td>
                    )}
                    {col('elec_prev_reading') && (
                      <td className="px-4 py-3 text-right">
                        {rec.elec_prev_reading}
                      </td>
                    )}
                    {col('elec_curr_reading') && (
                      <td className="px-4 py-3 text-right">
                        {rec.elec_curr_reading}
                      </td>
                    )}
                    {col('water_unit_price') && (
                      <td className="px-4 py-3 text-right">
                        {rec.water_unit_price}
                      </td>
                    )}
                    {col('water_prev_reading') && (
                      <td className="px-4 py-3 text-right">
                        {rec.water_prev_reading}
                      </td>
                    )}
                    {col('water_curr_reading') && (
                      <td className="px-4 py-3 text-right">
                        {rec.water_curr_reading}
                      </td>
                    )}
                    {col('created_at') && (
                      <td className="px-4 py-3 text-left">
                        {new Date(rec.created_at).toLocaleDateString()}
                      </td>
                    )}
                    {col('updated_at') && (
                      <td className="px-4 py-3 text-left">
                        {new Date(rec.updated_at).toLocaleDateString()}
                      </td>
                    )}
                    {col('payment_status') && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                            rec.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {rec.payment_status}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {rec.remark ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(rec)}
                        disabled={isLocked}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(rec.id)}
                        disabled={isLocked}
                        className="text-red-600 hover:bg-red-50 disabled:bg-transparent disabled:opacity-50"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div ref={sentinelRef} className="h-6" />
          {isFetchingNextPage && (
            <div className="py-2 text-center text-gray-500 text-sm">
              Loading more…
            </div>
          )}
        </Card>
      </Skeleton>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card
            className="w-full max-w-lg overflow-y-auto p-6"
            style={{ maxHeight: '90vh' }}
          >
            <h3 className="mb-4 font-semibold text-lg">
              {editingRecord ? 'Edit Utility Record' : 'New Utility Record'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="room_number">Room</Label>
                  <select
                    id="room_number"
                    title="Room"
                    value={form.room_number}
                    onChange={(e) =>
                      editingRecord
                        ? setForm({ ...form, room_number: e.target.value })
                        : handleRoomChange(e.target.value)
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select room</option>
                    {rooms.map((r) => (
                      <option key={r.room_number} value={r.room_number}>
                        {r.room_number}
                      </option>
                    ))}
                  </select>
                  {errors.room_number && (
                    <p className="mt-1 text-red-500 text-xs">
                      {errors.room_number}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contract_id">Contract</Label>
                  <select
                    id="contract_id"
                    title="Contract"
                    value={form.contract_id ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, contract_id: e.target.value || null })
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select contract</option>
                    {[...activeContracts]
                      .sort((a, b) =>
                        a.room_number.localeCompare(b.room_number),
                      )
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.room_number} — {c.id.slice(0, 8)}
                        </option>
                      ))}
                  </select>
                  {errors.contract_id && (
                    <p className="mt-1 text-red-500 text-xs">
                      {errors.contract_id}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="record_date">Record Date</Label>
                <DatePicker
                  id="record_date"
                  value={form.record_date}
                  onValueChange={(value) =>
                    setForm({ ...form, record_date: value })
                  }
                  className="mt-1 w-full text-sm"
                />
                {errors.record_date && (
                  <p className="mt-1 text-red-500 text-xs">
                    {errors.record_date}
                  </p>
                )}
              </div>

              <fieldset className="rounded-md border border-gray-200 p-3">
                <legend className="px-1 font-semibold text-gray-500 text-xs uppercase">
                  Electricity
                </legend>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {(
                    [
                      'elec_unit_price',
                      'elec_prev_reading',
                      'elec_curr_reading',
                    ] as const
                  ).map((key) => (
                    <div key={key}>
                      <Label htmlFor={key}>
                        {key.replace('elec_', '').replace(/_/g, ' ')}
                      </Label>
                      <Input
                        id={key}
                        type="number"
                        min="0"
                        step={key === 'elec_unit_price' ? '0.01' : '1'}
                        value={form[key]}
                        onChange={(e) =>
                          setForm({ ...form, [key]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
                {!editingRecord && latestRoomRecord?.record_date && (
                  <p className="mt-2 text-gray-400 text-xs">
                    Prev reading from{' '}
                    <span className="font-medium text-gray-500">
                      {latestRoomRecord.record_date}
                    </span>
                  </p>
                )}
              </fieldset>

              <fieldset className="rounded-md border border-gray-200 p-3">
                <legend className="px-1 font-semibold text-gray-500 text-xs uppercase">
                  Water
                </legend>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {(
                    [
                      'water_unit_price',
                      'water_prev_reading',
                      'water_curr_reading',
                    ] as const
                  ).map((key) => (
                    <div key={key}>
                      <Label htmlFor={key}>
                        {key.replace('water_', '').replace(/_/g, ' ')}
                      </Label>
                      <Input
                        id={key}
                        type="number"
                        min="0"
                        step={key === 'water_unit_price' ? '0.01' : '1'}
                        value={form[key]}
                        onChange={(e) =>
                          setForm({ ...form, [key]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
                {!editingRecord && latestRoomRecord?.record_date && (
                  <p className="mt-2 text-gray-400 text-xs">
                    Prev reading from{' '}
                    <span className="font-medium text-gray-500">
                      {latestRoomRecord.record_date}
                    </span>
                  </p>
                )}
              </fieldset>

              <div>
                <Label htmlFor="payment_status">Payment Status</Label>
                <select
                  id="payment_status"
                  title="Payment Status"
                  value={form.payment_status}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      payment_status: e.target.value as 'paid' | 'unpaid',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div>
                <Label htmlFor="remark">Remark</Label>
                <Input
                  id="remark"
                  type="text"
                  placeholder="Optional note"
                  value={form.remark ?? ''}
                  onChange={(e) => setForm({ ...form, remark: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelForm}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingRecord ? 'Save Changes' : 'Create Record'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved changes in the utility record form. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={showDiscardConfirm}
        title="Discard Record Changes?"
        description="You have unsaved changes in this form. Are you sure you want to discard them?"
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={() => {
          setShowDiscardConfirm(false)
          closeForm()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      {showMarkPaid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="mb-2 font-semibold text-lg">
              Mark Selected Records Paid
            </h3>
            <p className="mb-4 text-gray-600 text-sm">
              You are about to mark {selectedIds.length} record
              {selectedIds.length === 1 ? '' : 's'} as paid.
            </p>
            <div className="mb-4 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-gray-700 text-sm">
              <p className="font-medium text-gray-900">Rooms</p>
              <ul className="mt-2 grid list-disc grid-cols-1 gap-x-4 gap-y-1 pl-5 sm:grid-cols-3">
                {selectedRoomNumbers.map((room) => (
                  <li key={room}>{room}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowMarkPaid(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => markPaidMutation.mutate(selectedIds)}
                disabled={markPaidMutation.isPending}
              >
                Confirm Mark Paid
              </Button>
            </div>
          </Card>
        </div>
      )}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="mb-2 font-semibold text-lg">
              Delete Utility Record
            </h3>
            <p className="mb-6 text-gray-600 text-sm">
              Are you sure you want to delete this record? This cannot be
              undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => deleteMutation.mutate(deleteTarget)}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
